import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorContext } from '../context/ErrorContext';
import { UserContext } from '../context/UserContext';
import useSession from '../hooks/useSession';
import projectAPI from '../services/project';
import roleAPI from '../services/role';
import Toast from './layout/Toast';

const ShareProject = forwardRef(({ title, noBack, noMargin, noParticipants, noButton, showMore }, ref) => {

    const [people, setPeople] = useState([{
        email: '', role: ''
    }]);
    const [note, setNote] = useState('')
    const [invitationsSended, setInvitationsSended] = useState(false)
    const [invitationsSending, setInvitationsSending] = useState(false)
    const { idProject } = useParams()
    const navigate = useNavigate()
    const [session] = useSession()
    const [participants, setParticipants] = useState([])
    const [hashIdProject, setHashIdProject] = useState(null)
    const [error, setError] = useState([])
    const { parseErrors } = useContext(ErrorContext)
    const [more, setMore] = useState(false)
    const [l10n, setL10n] = useState(null)
    const { user, checkPermission, getL10nInstance } = useContext(UserContext)

    useEffect(() => {
      (async () => {
        const instance = await getL10nInstance();
        setL10n(instance);
      })();
    }, [session, user]);

    const onChange = (e, index) => {
        people[index][e.target.name] = e.target.value
        setPeople([...people])
    }

    const addPeople = () => {
        const newPeople = { email: '', role: '' }
        setPeople([...people, newPeople])
    }

    useImperativeHandle(ref, () => ({
        async externalSendInvitations(hash) {
            await sendInvitations(hash)
        }
    }))

    const sendInvitations = async (hash = null) => {
        const emails = []
        const types = []
        for (let item of people) {
            emails.push(item.email)
            types.push(item.role)
        }

        const payload = {
            hash_id_project: hash ? hash : hashIdProject,
            emails,
            types,
            note
        }

        try {
            setError([])
            setInvitationsSending(true)
            await projectAPI.invite({ jwt: session, payload })            
            setPeople([{
                email: '', role: ''
            }])
            setNote('')
            setInvitationsSended(true)
            const checkDelay = setInterval(() => {
                setInvitationsSended(false)
                clearInterval(checkDelay)
            }, 3000);
            getParticipants(hashIdProject)
        } catch (error) {
            const parsedErr = parseErrors(error.response.data.error)
            setError(parsedErr)
        }
        setInvitationsSending(false)
    }

    const onUpdate = async (e) => {
        setError([])
        if (e.target.value != 'remove') {
            try {
                const payload = {
                    hash_id_role: e.target.id,
                    type: e.target.value
                }
                await roleAPI.put({ jwt: session, payload })
                setError([{ message: "Participant role was successfully updated" }])
                getParticipants(hashIdProject)
            } catch (error) {
                console.error(error)
            }
        } else {
            onDelete(e.target.id)
        }
    }

    const onDelete = async (hashRole) => {
        try {
            const payload = {
                hash_id_role: hashRole
            }
            await roleAPI.del({ jwt: session, payload })
            setError([{ message: "Participant was successfully removed" }])
            getParticipants(hashIdProject)
        } catch (error) {
            console.error(error)
        }
    }

    const getParticipants = async (hash) => {
        const res = await projectAPI.participants({ jwt: session, hash })
        const parcedParticipants = res.data.data.roles.map((item) => {
            return {
                hash_role: item.hash_id_role,
                email: item.email,
                role: item.type == 'manager' ? 'collaborator' : item.type,
                is_am: item.position == 'am' ? true : false
            }
        })
        setParticipants(parcedParticipants)
    }

    useEffect(() => {
        (async () => {
            try {
                const hashProject = await projectAPI.project({ jwt: session, idProject })
                const hash = hashProject.data.data.hash_id_project
                setHashIdProject(hash)
                getParticipants(hash)
            } catch (error) {
                console.error(error)
            }
        })()
    }, [])

    useEffect(() => {
        if(showMore) {
            setMore(false)
        } else {
            setMore(true)
        }
    }, [showMore])

    const backButton = () => {
        return <button className='flex gap-1 text-[#A7A9B3] items-center text-base font-semibold' onClick={() => navigate('../widget#project/posted/' + idProject)}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.79683 10.4324L6.27651 10.9527C6.0562 11.173 5.69995 11.173 5.48198 10.9527L0.925732 6.39883C0.70542 6.17852 0.70542 5.82227 0.925732 5.6043L5.48198 1.04805C5.70229 0.827734 6.05854 0.827734 6.27651 1.04805L6.79683 1.56836C7.01948 1.79102 7.01479 2.1543 6.78745 2.37227L3.96323 5.06289H10.6992C11.0109 5.06289 11.2617 5.31367 11.2617 5.62539V6.37539C11.2617 6.68711 11.0109 6.93789 10.6992 6.93789H3.96323L6.78745 9.62852C7.01714 9.84648 7.02183 10.2098 6.79683 10.4324Z" fill="#A7A9B3" />
            </svg>
            <span className='font-helveticaNeue'>{l10n && l10n.getString('Back to project')}</span>
        </button>
    }

    const checkViewerRole = () => {
        return user.position == 'guest' && user.view && user.view.role == 'viewer'
    }

    const checkEditRole = (participant) => {
        return participant.is_am || !checkPermission('P-CHR')
    }

    const checkRemoveParticipant = () => {
        return user.position != 'guest' || (user.position == 'guest' && checkPermission('P-RMP'))
    }

    return (<>
        <div className={`${!noMargin && 'w-1/2 m-auto'} my-10 text-[#3D3F4B]`}>
            {!noBack && backButton()}
            <div>
                <h1 className={'text-slate-800 text-[32px] font-bold font-helveticaNeue leading-[38px] ' + (title ? 'text-left' : 'text-center')}>{title ? title : (l10n && l10n.getString('Invite people to your project'))}</h1>
                <div className='font-normal font-helveticaNeue text-neutral-500 tracking-tight my-8'>
                    <p>{l10n && l10n.getString('Invite members of your team to follow along with the production process')}:</p>
                    <br />
                    <ul className="list-disc list-inside">
                        <li><span className="font-bold">{l10n && l10n.getString('Collaborator')}:</span> {l10n && l10n.getString('Can approve or reject the project as well as request changes')}</li>
                        <li><span className="font-bold">{l10n && l10n.getString('Viewer')}:</span> {l10n && l10n.getString('View the project information, status and deliverables')}</li>
                    </ul>
                </div>
                {!more ?
                <button className='flex my-2 gap-1 text-[#734D85] items-center text-base font-semibold' onClick={() => setMore(true)}>+ {l10n && l10n.getString('Invite people')}</button> : <>
                <div className='rounded-[16px] border border-solid border-[#CCC] bg-white p-8'>
                    <div>
                        {people.map((item, i) =>
                            <div key={i} className='grid grid-cols-[5fr_3fr] gap-4'>
                                <div>
                                    {i == 0 ? <label className='block font-bold text-[14px] font-helveticeNeue'>{l10n && l10n.getString('Email')}</label> : <></>}
                                    <input id="email"
                                        className='block w-full h-12 rounded-[4.8px] my-2 px-[12px] py-[6px] border border-solid border-[#7C7C7C]'
                                        type="email"
                                        name="email"
                                        value={item.email}
                                        onChange={e => onChange(e, i)}
                                        required
                                    />
                                </div>
                                <div>
                                    {i == 0 ? <label className='block font-bold text-[14px] font-helveticeNeue'>{l10n && l10n.getString('Role')}</label> : <></>}
                                    <select id="role" name="role" value={item.role} onChange={e => onChange(e, i)}
                                        className="block w-full h-12 my-2 px-[12px] py-[6px] rounded-[4.8px] border border-solid border-[#7C7C7C] bg-transparent">
                                        <option className='text-gray-50' value="" disabled hidden>{l10n && l10n.getString('Select a role')}</option>
                                        {!checkViewerRole() && <option value="collaborator">{l10n && l10n.getString('Collaborator')}</option>}
                                        <option value="viewer">{l10n && l10n.getString('Viewer')}</option>
                                    </select>
                                </div>
                            </div>
                        )}
                        <button className='flex my-2 gap-1 text-[#734D85] items-center text-base font-semibold' onClick={addPeople}>+ {l10n && l10n.getString('Add another person')}</button>
                    </div>
                    <div>
                        <label className='block font-bold text-[14px] font-helveticeNeue'>{l10n && l10n.getString('Note')}</label>
                        <textarea id="note"
                            className='block w-full my-2 px-[12px] py-[6px] rounded-[4.8px] border border-solid border-[#7C7C7C]'
                            name="note"
                            rows="5"
                            value={note}
                            onChange={e => setNote(e.target.value)}
                        ></textarea>
                        <p className='font-[400] text-[12px] text-[#6D7081] font-helveticaNeue'>{l10n && l10n.getString('This note will be added as part of the email they’ll receive with your invitation.')}</p>

                        {!noButton && <>{!invitationsSended ? <button disabled={invitationsSending} type="button" onClick={() => sendInvitations()} className='h-12 my-8 w-[178px] rounded-[25px] font-semibold text-base text-white bg-[#6A4278] hover:bg-[#854f99] px-4 py-2 rounded'>{l10n && l10n.getString('Send invitation')}</button> :
                            <p className='font-semibold text-base text-[#14AE5C] my-8'>{l10n && l10n.getString('The participants were successfully added')}</p>}
                        <div className='font-normal text-base text-[#3D3F4B] flex gap-2 items-center'>
                            <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M11 2H12.5C13.3281 2 14 2.67188 14 3.5V5H0V3.5C0 2.67188 0.671875 2 1.5 2H3V0.5C3 0.225 3.225 0 3.5 0H4.5C4.775 0 5 0.225 5 0.5V2H9V0.5C9 0.225 9.225 0 9.5 0H10.5C10.775 0 11 0.225 11 0.5V2ZM0 14.5C0 15.3281 0.671875 16 1.5 16H12.5C13.3281 16 14 15.3281 14 14.5V6H0V14.5ZM2 8.5C2 8.225 2.225 8 2.5 8H5.5C5.775 8 6 8.225 6 8.5V11.5C6 11.775 5.775 12 5.5 12H2.5C2.225 12 2 11.775 2 11.5V8.5Z" fill="#B6B7C0" />
                            </svg>
                            <span className='text-[#6A6A6A] font-helveticaNeue'>{l10n && l10n.getString('Your invitation will expire in 30 days.')}</span>
                        </div></>}
                    </div>
                </div>
                </>}
                {!noParticipants && <>
                <hr className='my-12'></hr>
                <div className='my-8'>
                    <h1 className='text-slate-800 text-[32px] font-bold font-helveticaNeue leading-[38px] text-center mb-4'>{l10n && l10n.getString('Participants')}</h1>
                    <div className='rounded-[16px] border border-solid border-[#CCC] bg-white p-8'>
                        {participants.map((participant, index) => participant.role != 'creator' ?
                            <div key={index}
                                className="grid grid-cols-[3fr_1fr] gap-4 items-center">
                                <p className='font-helveticaNeue'>{participant.email} {participant.is_am ? <span className='bg-[#F6F6F6] font-helveticaNeue h-6 rounded-[25px] px-3 pt-[2px] pb-1 font-bold text-[12px] text-[#734D85] ml-2'>{l10n && l10n.getString('Account manager')}</span>: ''}</p>
                                <select id={participant.hash_role} name="current-role" value={participant.role} onChange={e => onUpdate(e)} disabled={checkEditRole(participant)}
                                    className="block w-full h-10 my-2 px-[12px] py-[6px] border border-solid border-[#B6B7C0] rounded bg-transparent font-helveticaNeue">
                                    <option value="collaborator">{l10n && l10n.getString('Collaborator')}</option>
                                    <option value="viewer">{l10n && l10n.getString('Viewer')}</option>
                                    { checkRemoveParticipant() && <option value="remove" className='text-red-600'>{l10n && l10n.getString('Remove')}</option>}
                                </select>
                            </div> : ''
                        )}
                    </div>
                </div></>}
            </div>
            {!noBack && backButton()}
        </div>
        <Toast toast={error} time={3000} />
    </>)
})

export default ShareProject